import type { InstructorAssignmentCubit } from '@breakoutlearning/firebase-repository/cubits/InstructorAssignmentCubit'
import type { SectionAssignment } from '@breakoutlearning/firebase-repository/models/SectionAssignment'
import { observer } from 'mobx-react-lite'
import { useCallback, useMemo, useRef } from 'react'
import { useDialogs } from 'hooks/dialogs'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { DateTime } from 'luxon'
import { AssignmentState } from '@breakoutlearning/firebase-repository/types'
import { noTryAsync } from '@breakoutlearning/firebase-repository/util'
import { toast } from 'react-hot-toast'
import { useTranslationTyped } from 'i18n/i18n'
import { LtiBaseAssignmentDialog } from './LtiBaseAssignmentDialog'
import { getAssignmentSchema } from 'pages/instructor/slide_deck/AssignDialogV3/schemas'
import type { AssignmentFormValues } from 'pages/instructor/slide_deck/AssignDialogV3/schemas'
import { deleteField } from 'firebase/firestore'
import { useLTIMode } from 'hooks/use-lti-mode'

export const LtiEditAssignmentDialog = observer(
  function LtiEditAssignmentDialog({
    assignment,
    cubit,
  }: {
    assignment: SectionAssignment
    cubit: InstructorAssignmentCubit
  }) {
    const { tt } = useTranslationTyped()
    const { popDialog } = useDialogs()
    const scalarsRef =
      useRef<SectionAssignment['data']['assignmentGradingScalars']>(undefined)
    const { ltiModeOn } = useLTIMode()

    const hasBeenStarted = cubit.hasBeenStarted
    const releasedToStudents =
      cubit.section.studentsIncludingRemovedCount > 0 &&
      assignment.data.assignmentState > 0
    const hasQuestions = cubit.questionsCollection.models.some(
      (q) => q.isGraded
    )
    const hasRubrics = cubit.slideRubrics.models.length > 0
    const isDraft = assignment.data.assignmentState <= AssignmentState.draft

    const schema = useMemo(() => {
      const expiresAt = assignment.data.expiresAt
      return getAssignmentSchema({
        tt,
        minExpiresAtOverride:
          cubit.hasBeenStarted && expiresAt
            ? {
                date: DateTime.fromJSDate(expiresAt),
                message: tt.instructor_assignment.can_only_be_moved_forward(),
              }
            : undefined,
        sectionEndDate: cubit.section.data.sectionEndDate,
        sectionStartDate: cubit.section.data.sectionStartDate,
      })
    }, [
      assignment.data.expiresAt,
      cubit.hasBeenStarted,
      tt,
      cubit.section.data.sectionStartDate,
      cubit.section.data.sectionEndDate,
    ])

    const form = useForm<AssignmentFormValues>({
      resolver: zodResolver(schema),
      mode: 'onChange',
      defaultValues: {
        catalogId: assignment.data.catalogId ?? undefined,
        customGroupingSelected: assignment.data.customGroupingSelected,
        assignedAt: assignment.assignedAt
          ? DateTime.fromJSDate(assignment.assignedAt)
          : undefined,
        expiresAt: assignment.expiresAt
          ? DateTime.fromJSDate(assignment.expiresAt)
          : undefined,
        assignmentType: assignment.assignmentType,
        groupingType: assignment.groupingType,
        sectionId: assignment.sectionId,
        groupingSize: assignment.maxGroupSize,
        groupingSizeMinimum: assignment.data.groupingSizeMinimum,
        groupingSizeMaximum: assignment.maxGroupSize,
        configureGrading:
          assignment.data.assignmentGradingScalars !== undefined,
        forbidStudentGroupCreation: assignment.data.forbidStudentGroupCreation,
        forbidStudentGroupScheduling:
          assignment.data.forbidStudentGroupScheduling,
        forbidStudentGroupSelection:
          assignment.data.forbidStudentGroupSelection,
      },
    })

    const onSubmit = useCallback(
      async (data: Partial<AssignmentFormValues>) => {
        const assignmentGradingScalars = data.configureGrading
          ? scalarsRef.current
          : undefined

        const finalPayload = {
          ...data,
          assignmentGradingScalars: assignmentGradingScalars ?? deleteField(),
        }

        // we don't want these to be sent to the server
        delete finalPayload.catalogId
        delete finalPayload.configureGrading

        const [, err] = await noTryAsync(async () => {
          await cubit.updateAssignment(finalPayload)
        })
        if (err) {
          toast.error(tt.instructor_assignment.save_failed())
          throw err
        }
        popDialog()
      },
      [cubit, popDialog, tt.instructor_assignment]
    )

    return (
      <LtiBaseAssignmentDialog
        form={form}
        onSubmit={onSubmit}
        releasedToStudents={releasedToStudents}
        hasBeenStarted={hasBeenStarted}
        hasQuestions={hasQuestions}
        hasRubrics={hasRubrics}
        submitButtonText={tt.instructor_class.save_text()}
        ltiMode={ltiModeOn}
        fullScreen={ltiModeOn}
        scalarsRef={scalarsRef}
        initialAssignmentGradingScalars={
          assignment.data.assignmentGradingScalars
        }
        disableGroupingReason={
          releasedToStudents && assignment.isExpired
            ? tt.instructor_assignment.groups.disabled_cannot_edit_grouping_expired_assignment()
            : undefined
        }
        isDraft={isDraft}
        submitButtonIncludeChevron={true}
      />
    )
  }
)
